class CustomLidCasting {
  // universal maximum number of custom-cast words per cookware item
  static MAX_WORDS = 5;

  constructor() {
    this.modal = document.getElementById('custom-lid-modal');
    if (!this.modal) return;

    this.config = window.customLidConfig || {};
    this.input = document.getElementById('custom-lid-text');
    this.wordCountEl = document.getElementById('custom-lid-word-count');
    this.priceEl = document.getElementById('custom-lid-price');
    this.confirmBtn = document.getElementById('custom-lid-confirm');
    this.skipBtn = document.getElementById('custom-lid-skip');
    this.closeBtn = document.getElementById('custom-lid-modal-close');
    this.errorEl = document.getElementById('custom-lid-error');
    this.loadingEl = document.getElementById('custom-lid-loading');
    this.productNameEl = document.getElementById('custom-lid-modal-product-name');

    this.pending = null;
    this.capturedForm = null;

    // cookware + cover combo elements
    this.comboBox = document.getElementById('custom-lid-combo');
    this.coverRow = document.getElementById('custom-lid-cover-row');
    this.coverCheck = document.getElementById('custom-lid-cover');
    this.coverPriceEl = document.getElementById('custom-lid-cover-price');
    this.plateRow = document.getElementById('custom-lid-plate-row');
    this.plateCheck = document.getElementById('custom-lid-plate');
    this.plateNameEl = document.getElementById('custom-lid-plate-name');
    this.platePriceEl = document.getElementById('custom-lid-plate-price');
    this.itemRow = document.getElementById('custom-lid-item-row');
    this.itemPriceEl = document.getElementById('custom-lid-item-price');
    this.extrasRow = document.getElementById('custom-lid-extras-row');
    this.extrasLabelEl = document.getElementById('custom-lid-extras-label');
    this.extrasPriceEl = document.getElementById('custom-lid-extras-price');
    this.totalRow = document.getElementById('custom-lid-total-row');
    this.totalEl = document.getElementById('custom-lid-total');
    if (this.coverCheck) this.coverCheck.addEventListener('change', () => this.updateTotals());
    if (this.plateCheck) this.plateCheck.addEventListener('change', () => this.updateTotals());

    this.init();
  }

  // ── Combo helpers ─────────────────────────────────────────────────────────
  comboFor(variantId) {
    const combo = this.config.combo;
    if (!combo) return null;
    const pid = combo.variantProduct?.[String(variantId)];
    const entry = pid != null ? combo.products?.[String(pid)] : null;
    return entry || null;
  }

  plateFor(variantId) {
    const combo = this.config.combo;
    return combo?.plateFor?.[String(variantId)] || null;
  }

  currentExtras() {
    const out = [];
    const entry = this.pending ? this.comboFor(this.pending.variantId) : null;
    const plate = this.pending ? this.plateFor(this.pending.variantId) : null;
    if (entry && this.coverRow && !this.coverRow.hidden && this.coverCheck?.checked) {
      // covers are size-matched to the pot: no cover for THIS size means no cover row
      const sized = this.config.combo?.coverFor?.[String(this.pending.variantId)];
      if (sized) out.push({ id: sized, cents: parseInt(entry.coverCents, 10) || 0, label: 'Fitted cover' });
    }
    if (plate && this.plateRow && !this.plateRow.hidden && this.plateCheck?.checked) {
      out.push({ id: plate.id, cents: plate.cents || 0, label: plate.title || 'Dutch Plate' });
    }
    return out;
  }

  setupCombo() {
    if (!this.comboBox) return;
    const entry = this.pending ? this.comboFor(this.pending.variantId) : null;
    const plate = this.pending ? this.plateFor(this.pending.variantId) : null;
    // a cover only exists for this pot when the SIZE-matched cover is in stock -
    // entry.coverVariant is the type-level fallback and must not stand in for it
    const sizedCover = this.pending ? this.config.combo?.coverFor?.[String(this.pending.variantId)] : null;
    const hasCover = !!(entry && sizedCover);
    const hasPlate = !!plate;
    this.comboBox.hidden = !(hasCover || hasPlate);
    if (this.coverRow) {
      this.coverRow.hidden = !hasCover;
      if (hasCover) {
        if (this.coverCheck) this.coverCheck.checked = true;
        const coverCents = parseInt(entry.coverCents, 10) || 0;
        const coverFull = parseInt(entry.coverFullCents, 10) || coverCents;
        const dealEl = document.getElementById('custom-lid-cover-deal');
        if (this.coverPriceEl) {
          if (coverFull > coverCents) {
            // combo discount active: show the solo price struck through
            this.coverPriceEl.innerHTML = '<s>' + this.formatMoney(coverFull) + '</s> ' + this.formatMoney(coverCents);
          } else {
            this.coverPriceEl.textContent = this.formatMoney(coverCents);
          }
        }
        if (dealEl) {
          const label = (this.config.combo && this.config.combo.discountLabel) || '';
          dealEl.hidden = !(coverFull > coverCents && label);
          if (!dealEl.hidden) dealEl.textContent = label;
        }
      }
    }
    if (this.plateRow) {
      this.plateRow.hidden = !hasPlate;
      if (hasPlate) {
        if (this.plateCheck) this.plateCheck.checked = false;
        if (this.plateNameEl && plate.title) this.plateNameEl.textContent = plate.title;
        if (this.platePriceEl) this.platePriceEl.textContent = this.formatMoney(plate.cents || 0);
      }
    }
    this.updateTotals();
  }

  updateTotals() {
    const combo = this.config.combo || {};
    const qty = this.pending?.quantity || 1;
    const potCents = this.pending ? (combo.variantCents?.[String(this.pending.variantId)] ?? null) : null;
    const extras = this.currentExtras();
    const extrasCents = extras.reduce((a, x) => a + x.cents, 0) * qty;
    const castingCents = this.getWords().length * (this.config.pricePerWordCents || 20000) * qty;
    if (this.itemRow) {
      this.itemRow.hidden = potCents == null;
      if (potCents != null && this.itemPriceEl) this.itemPriceEl.textContent = this.formatMoney(potCents * qty);
    }
    if (this.extrasRow) {
      this.extrasRow.hidden = extras.length === 0;
      if (extras.length && this.extrasPriceEl) {
        this.extrasPriceEl.textContent = this.formatMoney(extrasCents);
        if (this.extrasLabelEl) this.extrasLabelEl.textContent = extras.map((x) => x.label).join(' + ');
      }
    }
    if (this.totalRow) {
      this.totalRow.hidden = potCents == null;
      if (potCents != null && this.totalEl) this.totalEl.textContent = this.formatMoney(potCents * qty + extrasCents + castingCents);
    }
  }

  notifyAdded(extraNote) {
    ['cart:update', 'cart:refresh', 'cart:change'].forEach((n) => document.dispatchEvent(new CustomEvent(n)));
    if (typeof window.pcnShow === 'function' && this.pendingSnapshot) {
      window.pcnShow({
        product_title: this.pendingSnapshot.productTitle + (extraNote ? ' ' + extraNote : ''),
        quantity: this.pendingSnapshot.quantity || 1,
      });
    }
  }

  init() {
    /*
     * Capture phase on click fires BEFORE:
     *   - add-to-cart-component on:click="/handleClick"
     *   - product-form-component on:submit="/handleSubmit"
     *   - the legacy product-form submit listener
     *   - any custom carousel click listeners (e.g. ai_gen dual-product-carousel)
     *   - Shopify dynamic checkout / "Buy it now" buttons
     * This makes interception work for every add-to-cart mechanism in the theme.
     */
    document.addEventListener('click', this.onButtonClick.bind(this), true);

    this.input.addEventListener('input', this.onInput.bind(this));
    this.confirmBtn.addEventListener('click', this.onConfirm.bind(this));
    this.skipBtn.addEventListener('click', this.onSkip.bind(this));
    this.closeBtn.addEventListener('click', () => this.closeModal());
    this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.closeModal(); });
  }

  // ── Eligibility ─────────────────────────────────────────────────────────
  isEligibleVariant(id) {
    return Object.prototype.hasOwnProperty.call(this.config.eligibleVariants || {}, String(id));
  }

  isEligibleProduct(id) {
    return !!id && Object.prototype.hasOwnProperty.call(this.config.eligibleProducts || {}, String(id));
  }

  resolveProductTitle(variantId, productId, ancestorEl) {
    return this.config.eligibleVariants?.[String(variantId)]
      || this.config.eligibleProducts?.[String(productId)]
      || ancestorEl?.dataset.productTitle
      || '';
  }

  // ── Click capture ────────────────────────────────────────────────────────
  onButtonClick(e) {
    // ── 1. Homepage section "Customise & add to cart" buttons ──
    const trigger = e.target.closest('[data-custom-lid-trigger]');
    if (trigger) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.capturedForm = null;
      this.pending = {
        variantId: trigger.dataset.variantId,
        quantity: parseInt(trigger.dataset.quantity || '1', 10),
        productTitle: trigger.dataset.productTitle || '',
      };
      this.openModal();
      return;
    }

    // ── 2. Standard add-to-cart submit buttons (product pages, collection grid, quick-add) ──
    const btn = e.target.closest('button[name="add"]');
    if (btn) {
      const form = btn.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;

      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;

      const variantId = String(variantInput.value);

      // product-form-component or product-form both carry data-product-id
      const formComponent = form.closest('[data-product-id]');
      const productId = formComponent?.dataset.productId;

      // data-custom-lid-eligible is set on <product-info> in main-product.liquid
      const eligibleAncestor = form.closest('[data-custom-lid-eligible]');

      const eligible =
        this.isEligibleVariant(variantId) ||
        this.isEligibleProduct(productId) ||
        !!eligibleAncestor;

      if (!eligible) return;

      // Stop Shopify's component handlers from running
      e.preventDefault();
      e.stopImmediatePropagation();

      this.capturedForm = form;
      this.pending = {
        variantId,
        quantity: parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10),
        productTitle: this.resolveProductTitle(variantId, productId, eligibleAncestor),
      };
      this.openModal();
      return;
    }

    // ── 3. Custom carousel buttons (data-variant-id + data-product-id, no form wrapper) ──
    // Matches the AI-generated dual-product-carousel block and any similar custom components.
    const carouselBtn = e.target.closest('button[data-variant-id][data-product-id]');
    if (carouselBtn && !carouselBtn.disabled) {
      const variantId = String(carouselBtn.dataset.variantId);
      const productId = String(carouselBtn.dataset.productId);

      if (!this.isEligibleVariant(variantId) && !this.isEligibleProduct(productId)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      // Title from eligibility maps, falling back to the nearest heading in the card
      const productTitle =
        this.config.eligibleVariants?.[variantId] ||
        this.config.eligibleProducts?.[productId] ||
        carouselBtn.closest('[class*="card"]')?.querySelector('h2, h3, h4')?.textContent?.trim() ||
        '';

      this.capturedForm = null;
      this.pending = { variantId, quantity: 1, productTitle };
      this.openModal();
      return;
    }

    // ── 4. Sticky add-to-cart bar (product-information section) ──
    // The sticky bar button uses on:click="/handleAddToCartClick" with no form.
    // The current variant ID and product ID live on the sticky-add-to-cart element.
    const stickyBtn = e.target.closest('.sticky-add-to-cart__button');
    if (stickyBtn && !stickyBtn.disabled) {
      const stickyEl = stickyBtn.closest('sticky-add-to-cart');
      const variantId = stickyEl?.dataset.currentVariantId;
      const productId = stickyEl?.dataset.productId;

      if (!variantId || !productId) return;
      if (!this.isEligibleVariant(variantId) && !this.isEligibleProduct(productId)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const productTitle =
        this.config.eligibleVariants?.[variantId] ||
        this.config.eligibleProducts?.[productId] ||
        '';

      this.capturedForm = null;
      this.pending = { variantId: String(variantId), quantity: 1, productTitle };
      this.openModal();
      return;
    }

    // ── 5. Dynamic checkout / "Buy it now" buttons ──
    // Shopify renders these via {{ form | payment_button }} inside the product form.
    // They skip the cart and go straight to checkout - so on confirm/skip we add
    // to cart via API then redirect to /checkout instead of opening the drawer.
    const payBtn = e.target.closest('.shopify-payment-button__button');
    if (payBtn && !payBtn.disabled) {
      const form = payBtn.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;

      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;

      const variantId = String(variantInput.value);
      const formComponent = form.closest('[data-product-id]');
      const productId = formComponent?.dataset.productId;
      const eligibleAncestor = form.closest('[data-custom-lid-eligible]');

      const eligible =
        this.isEligibleVariant(variantId) ||
        this.isEligibleProduct(productId) ||
        !!eligibleAncestor;

      if (!eligible) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      this.capturedForm = null;
      this.pending = {
        variantId,
        quantity: parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10),
        productTitle: this.resolveProductTitle(variantId, productId, eligibleAncestor),
        goToCheckout: true,
      };
      this.openModal();
    }
  }

  // ── Modal ────────────────────────────────────────────────────────────────
  openModal() {
    this.input.value = '';
    this.setupCombo();
    this.onInput();
    this.productNameEl.textContent = this.pending?.productTitle || '';
    this.errorEl.classList.add('hidden');
    this.loadingEl.classList.add('hidden');
    this.setLoading(false);
    this.modal.showModal();
    requestAnimationFrame(() => this.input.focus());
  }

  closeModal() {
    this.modal.close();
    this.pending = null;
    this.capturedForm = null;
  }

  // ── Pricing ──────────────────────────────────────────────────────────────
  onInput() {
    const count = this.getWords().length;
    const over = count > CustomLidCasting.MAX_WORDS;
    const pricePerWordCents = this.config.pricePerWordCents || 20000;
    this.wordCountEl.textContent = count === 1 ? '1 word' : count + ' words';
    this.priceEl.textContent = this.formatMoney(Math.min(count, CustomLidCasting.MAX_WORDS) * pricePerWordCents);
    this.confirmBtn.disabled = count === 0 || over;
    const warn = document.getElementById('custom-lid-maxwarn');
    if (warn) warn.hidden = !over;
    // the summary's casting row only earns its place once there are words
    const castRow = document.getElementById('custom-lid-cast-row');
    if (castRow) castRow.hidden = count === 0;
    this.updateTotals();
  }

  getWords() {
    const text = this.input.value.trim();
    return text ? text.split(/\s+/) : [];
  }

  formatMoney(amountCents) {
    const format = this.config.moneyFormat || 'R{{ amount }}';
    const rand = amountCents / 100;
    const fmt = (n, d) => n.toLocaleString('en-ZA', { minimumFractionDigits: d, maximumFractionDigits: d });
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, fmt(rand, 0).replace(/\./g, ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, fmt(rand, 2).replace(/\./g, ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, fmt(rand, 0))
      .replace(/\{\{\s*amount\s*\}\}/g, fmt(rand, 2));
  }

  // ── Cart actions ──────────────────────────────────────────────────────────
  newGroupKey() {
    return 'cg-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  onSkip() {
    // Capture before closeModal() nulls them out
    const form = this.capturedForm;
    const pending = this.pending;
    const extras = this.currentExtras();
    this.pendingSnapshot = pending ? { ...pending } : null;
    this.closeModal();

    if (pending?.variantId && extras.length) {
      /*
       * Combo path - the pot ships with its cover (and optional plate), so add
       * everything together in one call. A hidden _combo group key links the
       * extras to their pot so the cart can remove them together.
       */
      const qty = pending.quantity || 1;
      const g = this.newGroupKey();
      const items = [{ id: String(pending.variantId), quantity: qty, properties: { '_combo_main': g } }];
      extras.forEach((x) => items.push({ id: String(x.id), quantity: qty, properties: { 'For': pending.productTitle || 'Potties cookware', '_combo_of': g } }));
      if (pending.goToCheckout) {
        this.skipBuyNow(items);
      } else {
        this.comboAdd(items, extras);
      }
      return;
    }

    if (form) {
      /*
       * Standard form path - hand control back to the theme by firing a native
       * submit event. requestSubmit() triggers product-form / product-form-component
       * handlers which do their own AJAX cart add and cart drawer update.
       */
      form.requestSubmit();
    } else if (pending?.goToCheckout && pending?.variantId) {
      /*
       * Buy it now path - add item without casting properties then go straight
       * to checkout, matching the original button's behaviour.
       */
      this.skipBuyNow([{ id: String(pending.variantId), quantity: pending.quantity || 1 }]);
    } else if (pending?.variantId) {
      /*
       * No form (e.g. custom carousel button) - add the item directly and
       * refresh the cart icon + drawer ourselves.
       */
      this.skipDirectAdd(String(pending.variantId), pending.quantity || 1);
    }
  }

  async comboAdd(items, extras) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error();
      this.notifyAdded(extras.length ? 'with ' + extras.map((x) => x.label.toLowerCase()).join(' + ') : '');
    } catch {
      // Multi-item add failed (e.g. an extra just sold out). Retry the pot alone,
      // telling the customer, rather than silently dropping the extras.
      try {
        const retry = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [items[0]] }),
        });
        if (!retry.ok) throw new Error();
        this.notifyAdded('(cover unavailable - added without it)');
      } catch {
        // Last resort: the cart page shows the true cart state.
        window.location.href = '/cart/add?id=' + items[0].id + '&quantity=' + items[0].quantity;
      }
    }
  }

  async skipDirectAdd(variantId, quantity) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
      if (!res.ok) throw new Error();
      await this.refreshCart();
      this.notifyAdded('');
    } catch {
      // Visible fallback: the native add endpoint lands on the cart page.
      window.location.href = '/cart/add?id=' + variantId + '&quantity=' + quantity;
    }
  }

  async skipBuyNow(items) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (res.ok) {
        window.location.href = '/checkout';
        return;
      }
    } catch {}
    // Add failed - send them to the cart to see what's actually there, not checkout.
    window.location.href = '/cart';
  }

  onConfirm() {
    const words = this.getWords();
    if (!words.length || words.length > CustomLidCasting.MAX_WORDS || !this.pending) return;

    this.pendingSnapshot = { ...this.pending };
    this.comboExtrasSnapshot = this.currentExtras();
    const goToCheckout = !!this.pending.goToCheckout;
    const castingText = words.join(' ');
    const wordCount = words.length;
    const pricePerWordCents = this.config.pricePerWordCents || 20000;
    const totalCents = wordCount * pricePerWordCents;
    const feeLabel = this.formatMoney(totalCents)
      + ' (' + wordCount + (wordCount === 1 ? ' word' : ' words')
      + ' \u00d7 ' + this.formatMoney(pricePerWordCents) + ')';

    // Shared hidden key linking the pot, its fee line and its combo extras, so
    // the cart can treat them as one item (removing the pot cleans up the rest).
    // Underscore-prefixed properties stay hidden in cart and checkout.
    const groupKey = this.newGroupKey();

    // Main cookware item with casting details as visible order properties
    const items = [{
      id: this.pending.variantId,
      quantity: this.pending.quantity,
      properties: {
        'Casting text': castingText,
        'Casting fee': feeLabel,
        '_casting_group': groupKey,
        '_combo_main': groupKey,
      },
    }];

    /*
     * Casting fee line item - adds the real ZAR amount to the cart total.
     * Requires a "Custom Lid Casting Fee" product (R200/unit) to be created
     * and configured in Theme Settings > Custom lid casting > Casting fee product.
     * One unit is added per word cast.
     */
    if (this.config.castingFeeVariantId) {
      items.push({
        id: this.config.castingFeeVariantId,
        // one fee unit per word, per pot - qty 2 personalised pots pay twice
        quantity: wordCount * (this.pending.quantity || 1),
        properties: {
          'For': this.pending.productTitle,
          'Casting text': castingText,
          '_casting_group': groupKey,
          },
      });
    }

    // cookware + cover combo: include the fitted cover / Dutch plate unless removed
    const comboQty = this.pending.quantity || 1;
    this.comboExtrasSnapshot.forEach((x) => {
      items.push({ id: String(x.id), quantity: comboQty, properties: { 'For': this.pending.productTitle || 'Potties cookware', '_combo_of': groupKey } });
    });

    this.setLoading(true);
    this.submitCart(items, goToCheckout);
  }

  async submitCart(items, goToCheckout = false) {
    this.errorEl.classList.add('hidden');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        // Surface Shopify's actual reason (e.g. an item just sold out) instead
        // of the generic message, so the customer knows what to change.
        let msg = '';
        try {
          const err = await res.json();
          msg = err.description || err.message || '';
        } catch {}
        throw new Error(msg);
      }
      this.closeModal();
      if (goToCheckout) {
        window.location.href = '/checkout';
      } else {
        this.notifyAdded('with casting');
        await this.refreshCart();
      }
    } catch (e) {
      this.setLoading(false);
      if (e && e.message) {
        const detail = this.errorEl.querySelector('[data-error-detail]');
        if (detail) detail.textContent = e.message;
        else this.errorEl.textContent = this.errorEl.textContent.split(' — ')[0] + ' — ' + e.message;
      }
      this.errorEl.classList.remove('hidden');
    }
  }

  async refreshCart() {
    try {
      const res = await fetch('/?sections=cart-icon-bubble,cart-drawer');
      const sections = await res.json();
      if (sections['cart-icon-bubble']) {
        const el = document.getElementById('cart-icon-bubble');
        if (el) el.innerHTML = sections['cart-icon-bubble'];
      }
      if (sections['cart-drawer']) {
        const el = document.getElementById('CartDrawer');
        if (el) el.innerHTML = sections['cart-drawer'];
      }
    } catch {}

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    document.querySelector('#cart-icon-bubble a')?.click();
  }

  setLoading(on) {
    this.confirmBtn.disabled = on || this.getWords().length === 0;
    this.skipBtn.disabled = on;
    this.loadingEl.classList.toggle('hidden', !on);
  }
}

document.addEventListener('DOMContentLoaded', () => new CustomLidCasting());
